import { useState, useEffect } from "react";
import { Link2, Copy, Check, Share2 } from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import SelectDropdown from "../ui/SelectDropdown";
import { toast } from "react-toastify";
import { generateInviteLink } from "../../services/inviteService";
import { getSocieties } from "../../services/societyService";
import { normalizePropertyType, PROPERTY_CATEGORIES } from "../../utils/propertyStructure";

const InviteUserModal = ({ isOpen, onClose, initialSocietyId = null, initialRole = null }) => {
  const [societies, setSocieties] = useState([]);
  const [selectedSociety, setSelectedSociety] = useState(initialSocietyId ? String(initialSocietyId) : "");
  const [targetRole, setTargetRole] = useState(initialRole || "resident");
  const [validityDays, setValidityDays] = useState("7");
  const [maxUsage, setMaxUsage] = useState("1");
  const [notes, setNotes] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientName, setRecipientName] = useState("");

  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [smsSent, setSmsSent] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setGeneratedLink(null);
      setSmsSent(false);
      setCopied(false);
      setNotes("");
      setRecipientPhone("");
      setRecipientName("");
      if (initialSocietyId) {
        setSelectedSociety(String(initialSocietyId));
      }
      if (initialRole) {
        setTargetRole(initialRole);
      }
      loadSocieties();
    }
  }, [isOpen, initialSocietyId, initialRole]);

  const loadSocieties = async () => {
    try {
      const data = await getSocieties();
      const list = Array.isArray(data) ? data : data?.societies || [];
      setSocieties(list);
      if (!selectedSociety && list.length > 0) {
        setSelectedSociety(String(list[0].society_id));
      }
    } catch (err) {
      console.error("Failed to load societies for invite:", err);
    }
  };

  const handleGenerate = async () => {
    if (!selectedSociety) {
      toast.warn("Please select a property");
      return;
    }

    setLoading(true);
    try {
      const hours = parseInt(validityDays, 10) * 24;
      const res = await generateInviteLink({
        token_type: "ONBOARDING",
        society_id: parseInt(selectedSociety, 10),
        target_role: targetRole,
        max_usage: parseInt(maxUsage, 10) || 1,
        validity_hours: hours,
        recipient_phone: recipientPhone.trim() || undefined,
        recipient_name: recipientName.trim() || undefined,
        payload_json: {
          note: notes.trim() || undefined,
          role_label: targetRole,
        },
      });

      if (res.success && res.inviteUrl) {
        setGeneratedLink(res.inviteUrl);
        setSmsSent(Boolean(res.smsSent));
        if (res.smsSent) {
          toast.success("Invite link generated & DLT SMS sent successfully!");
        } else {
          toast.success("Invite link generated!");
        }
      } else {
        toast.error(res.message || "Failed to generate invite");
      }
    } catch (err) {
      console.error("Generate invite error:", err);
      toast.error(err.response?.data?.message || "Failed to generate invite link");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    toast.info("Invite link copied to clipboard!");
    setTimeout(() => setCopied(false), 3000);
  };

  const handleShareWhatsApp = () => {
    if (!generatedLink) return;
    const soc = societies.find((s) => String(s.society_id) === String(selectedSociety));
    const socName = soc?.society_name || "AsmitA";
    const nameStr = recipientName.trim() || "there";
    const text = encodeURIComponent(
      `Dear ${nameStr}, you are invited to join My AsmitA for ${socName}.\nDownload the app to access your property services and updates:\n${generatedLink}`
    );
    window.open(`https://api.whatsapp.com/send?text=${text}`, "_blank");
  };

  const currentSociety = societies.find((s) => String(s.society_id) === String(selectedSociety));
  const normalizedCategory = normalizePropertyType(currentSociety?.society_type);

  const getRoleOptionsForCategory = (category) => {
    switch (category) {
      case PROPERTY_CATEGORIES.INDUSTRIAL:
        return [
          { value: "corporate-employee", label: "Corporate Employee" },
          { value: "department-head", label: "Department Head (HOD)" },
          { value: "security-guard", label: "Security Guard" },
          { value: "facility-manager", label: "Facility / Property Manager" },
          { value: "society-staff", label: "Property Staff / Facility" },
        ];
      case PROPERTY_CATEGORIES.COMMERCIAL:
        return [
          { value: "owner", label: "Commercial Store / Unit Owner" },
          { value: "tenant", label: "Commercial Tenant / Business Staff" },
          { value: "security-guard", label: "Security Guard" },
          { value: "facility-manager", label: "Facility / Property Manager" },
          { value: "society-staff", label: "Property Staff / Facility" },
        ];
      case PROPERTY_CATEGORIES.HYBRID:
        return [
          { value: "resident", label: "Resident" },
          { value: "owner", label: "Property / Flat / Store Owner" },
          { value: "tenant", label: "Tenant / Business Staff" },
          { value: "security-guard", label: "Security Guard" },
          { value: "facility-manager", label: "Facility / Property Manager" },
          { value: "society-staff", label: "Property Staff / Facility" },
          { value: "corporate-employee", label: "Corporate Employee" },
        ];
      case PROPERTY_CATEGORIES.RESIDENTIAL:
      default:
        return [
          { value: "resident", label: "Resident" },
          { value: "owner", label: "Flat Owner" },
          { value: "tenant", label: "Tenant" },
          { value: "security-guard", label: "Security Guard" },
          { value: "facility-manager", label: "Facility / Property Manager" },
          { value: "society-staff", label: "Property Staff / Facility" },
        ];
    }
  };

  const roleOptions = getRoleOptionsForCategory(normalizedCategory);

  // Keep targetRole valid when switching property types
  useEffect(() => {
    if (roleOptions.length > 0 && !roleOptions.some((r) => r.value === targetRole)) {
      setTargetRole(roleOptions[0].value);
    }
  }, [selectedSociety, roleOptions, targetRole]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ background: "#eff6ff", color: "#2563eb", padding: 6, borderRadius: 8, display: "flex" }}>
            <Link2 size={18} />
          </div>
          <span>Invite New User to Property</span>
        </div>
      }
      size="md"
    >
      <div style={{ padding: "8px 0" }}>
        {!generatedLink ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                Select Property <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <SelectDropdown
                value={selectedSociety}
                onChange={setSelectedSociety}
                options={societies.map((s) => ({
                  value: String(s.society_id),
                  label: `${s.society_name} (${s.society_type || "Residential"})`,
                }))}
                placeholder="Choose property..."
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                  Target Role
                </label>
                <SelectDropdown
                  value={targetRole}
                  onChange={setTargetRole}
                  options={roleOptions}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                  Link Validity
                </label>
                <SelectDropdown
                  value={validityDays}
                  onChange={setValidityDays}
                  options={[
                    { value: "1", label: "24 Hours" },
                    { value: "3", label: "3 Days" },
                    { value: "7", label: "7 Days (Recommended)" },
                    { value: "30", label: "30 Days" },
                  ]}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                  Direct SMS Mobile (Optional)
                </label>
                <input
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                  Recipient Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Rahul Sharma"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                Maximum Uses
              </label>
              <SelectDropdown
                value={maxUsage}
                onChange={setMaxUsage}
                options={[
                  { value: "1", label: "Single Use (1 person)" },
                  { value: "5", label: "Up to 5 people" },
                  { value: "50", label: "Multi-use (Up to 50 people)" },
                  { value: "500", label: "Batch Onboarding (Up to 500)" },
                ]}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                Note / Unit Reference (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Unit 402 or Facility staff"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #cbd5e1",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
              📱 <strong>DLT SMS Template Active:</strong> Entering a mobile number automatically dispatches the registered SMS (ID: <code>1677100000000395667</code>) directly to the user.
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={loading}>
                {loading ? "Generating Link..." : recipientPhone.trim() ? "Generate & Send SMS" : "Generate Invite Link"}
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#dcfce7",
                color: "#16a34a",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <Check size={24} />
            </div>

            <h3 style={{ margin: "0 0 6px", fontSize: 18, color: "#0f172a" }}>Invite Link Created</h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
              {smsSent
                ? "DLT SMS invitation dispatched to recipient. You can also share the link manually below."
                : "Share this link with the user. It will open the app directly or direct them to download it."}
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "#f1f5f9",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 16,
              }}
            >
              <input
                type="text"
                readOnly
                value={generatedLink}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  fontSize: 13,
                  fontFamily: "monospace",
                  color: "#0f172a",
                }}
              />
              <button
                onClick={handleCopy}
                style={{
                  background: copied ? "#22c55e" : "#0284c7",
                  color: "white",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <Button variant="outline" onClick={handleShareWhatsApp} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Share2 size={16} /> Share via WhatsApp
              </Button>
              <Button onClick={() => setGeneratedLink(null)}>
                Create Another Link
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default InviteUserModal;
